import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search, Loader2, CheckCircle, XCircle, Clock, CalendarDays, Users, Baby, ScanLine, BarChart3, QrCode, CalendarOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";
import QRScanner from "@/components/attendance/QRScanner";
import AttendanceReports from "@/components/attendance/AttendanceReports";
import QRCardGenerator from "@/components/attendance/QRCardGenerator";
import LeaveRequestsManager from "@/components/attendance/LeaveRequestsManager";

type AttendanceStatus = "present" | "absent" | "late";

const statusConfig: Record<AttendanceStatus, { label: string; icon: typeof CheckCircle; badgeClass: string }> = {
  present: { label: "حاضر", icon: CheckCircle, badgeClass: "gradient-hero border-0" },
  absent: { label: "غائب", icon: XCircle, badgeClass: "" },
  late: { label: "متأخر", icon: Clock, badgeClass: "bg-yellow-500 border-0 hover:bg-yellow-600" },
};

const Attendance = () => {
  const { profile } = useAuth();
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [childrenList, setChildrenList] = useState<any[]>([]);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [childAttendance, setChildAttendance] = useState<Record<string, AttendanceStatus>>({});
  const [staffAttendance, setStaffAttendance] = useState<Record<string, AttendanceStatus>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchChildren, setSearchChildren] = useState("");
  const [searchStaff, setSearchStaff] = useState("");
  const [mainTab, setMainTab] = useState("manual");

  const fetchData = async () => {
    if (!profile?.nursery_id) return;
    setLoading(true);

    const [childrenRes, staffRes, attendanceRes] = await Promise.all([
      supabase.from("children").select("id, full_name, classes(name)").eq("nursery_id", profile.nursery_id).order("full_name"),
      supabase.from("staff").select("id, full_name, role").eq("nursery_id", profile.nursery_id).order("full_name"),
      supabase.from("attendance").select("*").eq("nursery_id", profile.nursery_id).eq("date", selectedDate),
    ]);

    setChildrenList(childrenRes.data || []);
    setStaffList(staffRes.data || []);

    const childMap: Record<string, AttendanceStatus> = {};
    const staffMap: Record<string, AttendanceStatus> = {};
    (attendanceRes.data || []).forEach((a: any) => {
      if (a.attendee_type === "child") childMap[a.attendee_id] = a.status as AttendanceStatus;
      if (a.attendee_type === "staff") staffMap[a.attendee_id] = a.status as AttendanceStatus;
    });
    setChildAttendance(childMap);
    setStaffAttendance(staffMap);
    setLoading(false);
  };

  useEffect(() => {
    if (profile?.nursery_id) fetchData();
  }, [profile?.nursery_id, selectedDate]);

  const saveAttendance = async (type: "child" | "staff") => {
    if (!profile?.nursery_id) return;
    setSaving(true);

    const map = type === "child" ? childAttendance : staffAttendance;
    const entries = Object.entries(map);

    if (entries.length === 0) {
      toast.error("لا يوجد حضور لحفظه");
      setSaving(false);
      return;
    }

    const records = entries.map(([attendee_id, status]) => ({
      nursery_id: profile.nursery_id!,
      attendee_type: type,
      attendee_id,
      date: selectedDate,
      status,
      method: "manual",
    }));

    await supabase
      .from("attendance")
      .delete()
      .eq("nursery_id", profile.nursery_id)
      .eq("attendee_type", type)
      .eq("date", selectedDate);

    const { error } = await supabase.from("attendance").insert(records);

    setSaving(false);
    if (error) {
      toast.error("خطأ في حفظ الحضور: " + error.message);
    } else {
      toast.success(`تم حفظ حضور ${type === "child" ? "الأطفال" : "الموظفين"} بنجاح!`);
    }
  };

  const markAll = (
    list: any[],
    status: AttendanceStatus,
    setFn: React.Dispatch<React.SetStateAction<Record<string, AttendanceStatus>>>
  ) => {
    const map: Record<string, AttendanceStatus> = {};
    list.forEach((item) => (map[item.id] = status));
    setFn(map);
  };

  const getStats = (map: Record<string, AttendanceStatus>, total: number) => {
    const present = Object.values(map).filter((s) => s === "present").length;
    const absent = Object.values(map).filter((s) => s === "absent").length;
    const late = Object.values(map).filter((s) => s === "late").length;
    const unmarked = total - Object.keys(map).length;
    return { present, absent, late, unmarked };
  };

  const filteredChildren = childrenList.filter((c) => c.full_name.includes(searchChildren));
  const filteredStaff = staffList.filter((s) => s.full_name.includes(searchStaff));
  const childStats = getStats(childAttendance, childrenList.length);
  const staffStats = getStats(staffAttendance, staffList.length);

  const roleLabels: Record<string, string> = {
    teacher: "مدرس/ة", supervisor: "مشرف/ة", admin: "إداري/ة", driver: "سائق", cleaner: "عامل نظافة",
  };

  const renderAttendanceRow = (
    item: any,
    type: "child" | "staff",
    map: Record<string, AttendanceStatus>,
    setFn: React.Dispatch<React.SetStateAction<Record<string, AttendanceStatus>>>
  ) => {
    const status = map[item.id];
    return (
      <tr key={item.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
        <td className="p-3 font-medium text-card-foreground">{item.full_name}</td>
        <td className="p-3 text-muted-foreground text-xs">
          {type === "child" ? item.classes?.name || "-" : roleLabels[item.role] || item.role}
        </td>
        <td className="p-3">
          <div className="flex items-center gap-2">
            {(["present", "absent", "late"] as AttendanceStatus[]).map((s) => {
              const sc = statusConfig[s];
              const isActive = status === s;
              return (
                <Button
                  key={s}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  className={`h-8 px-3 text-xs ${isActive ? (s === "present" ? "gradient-hero border-0" : s === "late" ? "bg-yellow-500 border-0 hover:bg-yellow-600 text-primary-foreground" : "bg-destructive border-0 hover:bg-destructive/90") : ""}`}
                  onClick={() => setFn((prev) => ({ ...prev, [item.id]: s }))}
                >
                  <sc.icon className="w-3 h-3 ml-1" />
                  {sc.label}
                </Button>
              );
            })}
          </div>
        </td>
        <td className="p-3">
          <QRCardGenerator
            id={item.id}
            name={item.full_name}
            type={type}
            role={type === "staff" ? roleLabels[item.role] || item.role : undefined}
          />
        </td>
      </tr>
    );
  };

  const StatsBar = ({ stats, total }: { stats: ReturnType<typeof getStats>; total: number }) => (
    <div className="flex items-center gap-4 text-sm flex-wrap">
      <span className="flex items-center gap-1 text-primary">
        <CheckCircle className="w-4 h-4" /> حاضر: {stats.present}
      </span>
      <span className="flex items-center gap-1 text-destructive">
        <XCircle className="w-4 h-4" /> غائب: {stats.absent}
      </span>
      <span className="flex items-center gap-1 text-yellow-600">
        <Clock className="w-4 h-4" /> متأخر: {stats.late}
      </span>
      {stats.unmarked > 0 && (
        <span className="text-muted-foreground">غير محدد: {stats.unmarked}</span>
      )}
      <span className="text-muted-foreground mr-auto">الإجمالي: {total}</span>
    </div>
  );

  const renderManualTab = (
    type: "child" | "staff",
    list: any[],
    filtered: any[],
    map: Record<string, AttendanceStatus>,
    setFn: React.Dispatch<React.SetStateAction<Record<string, AttendanceStatus>>>,
    stats: ReturnType<typeof getStats>,
    search: string,
    setSearch: (v: string) => void
  ) => (
    <div className="space-y-4">
      <StatsBar stats={stats} total={list.length} />
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="البحث..." className="pr-10" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button variant="outline" size="sm" onClick={() => markAll(list, "present", setFn)}>
          <CheckCircle className="w-3 h-3 ml-1" /> تحضير الجميع
        </Button>
        <Button variant="outline" size="sm" onClick={() => markAll(list, "absent", setFn)}>
          <XCircle className="w-3 h-3 ml-1" /> تغييب الجميع
        </Button>
      </div>

      {filtered.length === 0 ? (
        <Card className="shadow-card">
          <CardContent className="py-12 text-center">
            {type === "child" ? <Baby className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" /> : <Users className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />}
            <p className="text-muted-foreground">لا يوجد {type === "child" ? "أطفال" : "موظفين"} مسجلين بعد</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-right p-3 font-medium text-muted-foreground">الاسم</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">{type === "child" ? "الفصل" : "الوظيفة"}</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">الحالة</th>
                    <th className="text-right p-3 font-medium text-muted-foreground w-12">QR</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => renderAttendanceRow(item, type, map, setFn))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button className="gradient-hero border-0 px-8" onClick={() => saveAttendance(type)} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
          حفظ حضور {type === "child" ? "الأطفال" : "الموظفين"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">تسجيل الحضور والغياب</h1>
          <p className="text-muted-foreground text-sm mt-1">تسجيل الحضور يدوياً أو عبر مسح QR / باركود</p>
        </div>
        {mainTab === "manual" && (
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-muted-foreground" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-44"
              dir="ltr"
            />
          </div>
        )}
      </div>

      {/* Main Tabs: Manual / Scanner / Reports */}
      <Tabs value={mainTab} onValueChange={setMainTab} dir="rtl">
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="manual" className="gap-1.5">
            <CalendarDays className="w-4 h-4" /> يدوي
          </TabsTrigger>
          <TabsTrigger value="scanner" className="gap-1.5">
            <ScanLine className="w-4 h-4" /> مسح ضوئي
          </TabsTrigger>
          <TabsTrigger value="leaves" className="gap-1.5">
            <CalendarOff className="w-4 h-4" /> الإجازات
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-1.5">
            <BarChart3 className="w-4 h-4" /> التقارير
          </TabsTrigger>
        </TabsList>

        {/* Manual Tab */}
        <TabsContent value="manual" className="mt-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <Tabs defaultValue="children" dir="rtl">
              <TabsList>
                <TabsTrigger value="children" className="gap-1">
                  <Baby className="w-4 h-4" /> الأطفال ({childrenList.length})
                </TabsTrigger>
                <TabsTrigger value="staff" className="gap-1">
                  <Users className="w-4 h-4" /> الموظفين ({staffList.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="children" className="mt-4">
                {renderManualTab("child", childrenList, filteredChildren, childAttendance, setChildAttendance, childStats, searchChildren, setSearchChildren)}
              </TabsContent>

              <TabsContent value="staff" className="mt-4">
                {renderManualTab("staff", staffList, filteredStaff, staffAttendance, setStaffAttendance, staffStats, searchStaff, setSearchStaff)}
              </TabsContent>
            </Tabs>
          )}
        </TabsContent>

        {/* Scanner Tab */}
        <TabsContent value="scanner" className="mt-4">
          <QRScanner />
        </TabsContent>

        {/* Leaves Tab */}
        <TabsContent value="leaves" className="mt-4">
          <LeaveRequestsManager />
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-4">
          <AttendanceReports />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Attendance;
